/*
 *
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import React from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { usePageConfigUser } from 'hooks/usePageConfig'
import * as measureTypeUtil from 'utils/measureTypeUtil'
import { fromSearchCrosstabResult } from 'utils/timelineDataUtil'
import {
  getSeriesType,
  getStacking,
  getYAxisGroup,
  getYAxisInfo,
  getZIndex
} from 'utils/highchartsUtil'
import { addTz } from 'utils/timeZoneUtil'
import PlaceHolder from 'components/share/PlaceHolder'
import LineChart from 'components/visualization/Line'
import MetricSeries, { MetricSeriesStrings } from 'enums/MetricSeries'
import { TimeUnit } from 'enums/Charts'
import { YAxisGroup } from 'enums'
import messageUtil from 'utils/messageUtil'
import { SocialWebTimelineComparisonWidget } from 'schemas/SocialWebTimelineComparisonWidgetSchema'
import { PivotResult } from 'schemas/PivotResultSchema'
import { Widget } from 'features/types'
import { LineSeries, YAxisInfo } from 'components/visualization/Line/types'
import { getSeriesColor } from '../../../utils'
import { getSeriesLabel } from '../../utils'
import { TimelineComparisonWidget } from '../../../types'

const isSocialWebTimelineComparisonWidget = (
  widgetSetting: TimelineComparisonWidget
): widgetSetting is SocialWebTimelineComparisonWidget => 'metricNames' in widgetSetting

const Timeline: Widget = ({ widgetId }) => {
  const widgetSetting = useDashboardWidgetSetting<TimelineComparisonWidget>(widgetId)
  const widgetData = useDashboardWidgetData<PivotResult>(widgetId)
  const pageConfigUser = usePageConfigUser()

  const { data } = widgetData

  // TODO: We should filter the result by checking the user VOC permission here.
  // There is a bug in app-cb-closure version. It never works.
  // We should fix it if we consider it is a critical bug

  const settingMeasures = widgetSetting.measureTypes || []
  const settingMetricNames = isSocialWebTimelineComparisonWidget(widgetSetting)
    ? widgetSetting.metricNames || []
    : []

  let settingSeriesType: MetricSeriesStrings[] = []
  for (let i = 0; i < settingMeasures.length; i += 1) {
    settingSeriesType = settingSeriesType.concat(
      measureTypeUtil.getMetricSeries(settingMeasures[i]!)
    )
  }
  if (settingMetricNames && settingMetricNames.length > 0) {
    settingSeriesType = settingSeriesType.concat(settingMetricNames as MetricSeriesStrings[])
  }

  const timelineData = fromSearchCrosstabResult(
    data,
    widgetSetting,
    settingSeriesType,
    pageConfigUser.timeZone
  )
  const measureResult = fromSearchCrosstabResult(
    data,
    widgetSetting,
    [MetricSeries.TOTAL_BUZZ],
    pageConfigUser.timeZone
  )

  const { columns, series: seriesSet } = timelineData
  const formattedSeriesSet: LineSeries[] = []
  const SeriesColorSettings: { [key: string]: string } = {}
  const yAxisInfoList: YAxisInfo[] = []
  const notSupportedTopicIds: number[] = []

  seriesSet!.forEach((series) => {
    let yAxisGroup = getYAxisGroup(
      series.type as MetricSeriesStrings,
      series.category!,
      widgetSetting.analysisType!
    )
    const yAxisInfo = getYAxisInfo(
      series.type as MetricSeriesStrings,
      series.category!,
      widgetSetting.analysisType!
    )

    let found = false
    if (yAxisGroup === YAxisGroup.NONE) {
      yAxisGroup = YAxisGroup.SW_BUZZ
      found = true
    } else {
      const yAxisInfoIdx = yAxisInfoList.findIndex((yAxisInfoObj) => yAxisGroup === yAxisInfoObj.id)
      if (yAxisInfoIdx > -1) {
        found = true
        if (
          yAxisInfoList[yAxisInfoIdx].title.text !==
          measureTypeUtil.getLabel(series.type as MetricSeriesStrings, widgetSetting.analysisType)
        ) {
          yAxisInfoList[yAxisInfoIdx].title.text = messageUtil.timelineYAxisGroupTitle(yAxisGroup)!
        }
      }
    }
    if (!found) {
      // only one yAxis will stay on the left
      if (yAxisInfoList.length === 0) yAxisInfo.opposite = false
      yAxisInfoList.push(yAxisInfo)
    }

    // generate series
    const seriesData = series.data!.map((yData, idx) => ({
      x: addTz(columns![idx]!, pageConfigUser.timeZone),
      y: series.isNegative && yData !== 0 ? yData! * -1 : yData // To prevent negative zero '-0'
    }))

    // CB-20806, New Summary Metrics | Suggest to disable the timeline for
    // the "-" in children metrics
    let isDash = false
    if (
      series.isChild &&
      ([
        MetricSeries.IMPRESSIONS,
        MetricSeries.NET_SENTIMENT,
        MetricSeries.PASSION
      ] as string[]).includes(series.type!)
    ) {
      if (measureResult && measureResult.series) {
        for (let j = 0; j < measureResult.series.length; j += 1) {
          if (measureResult.series[j].category === series.category) {
            if (measureResult.series[j].data) {
              const measureData = measureResult.series[j].data
              const measureString = measureData!.join('')
              isDash = measureData!.length === measureString.length && !/[^0]/.exec(measureString)
            }
            break
          }
        }
      }
    }

    const isVisible = series.isSupported && !series.isNoCount && !isDash

    const formattedSeries = {
      name: getSeriesLabel(series, widgetSetting),
      data: seriesData,
      yAxis: yAxisGroup,
      type: getSeriesType(series),
      stacking: getStacking(series, false),
      zIndex: getZIndex(getSeriesType(series)),
      visible: isVisible
    } as LineSeries
    formattedSeriesSet.push(formattedSeries)

    const seriesColor = getSeriesColor(series, widgetSetting.analysisType!)
    if (seriesColor) {
      SeriesColorSettings[formattedSeries.name!] = seriesColor
    }

    if (!series.isSupported) {
      notSupportedTopicIds.push(series.topicId!)
    }
  })

  // end of for seriesSet loop
  // CB-20311, Summary Metrics | Cannot show Net Sentiment timeline with 0%
  // Therefore, to use measure count to determine if it is a real empty data
  let hasMeasureData = false
  if (measureResult && measureResult.series && timelineData.series!.length > 0) {
    for (let i = 0; i < measureResult.series.length; i += 1) {
      const measureSeries = measureResult.series[i]
      if (!notSupportedTopicIds.includes(measureSeries.topicId!) && measureSeries.data) {
        const measureData = measureSeries.data
        const measureString = measureData.join('')
        hasMeasureData = measureData.length !== measureString.length || !!/[^0]/.exec(measureString)
        if (hasMeasureData) {
          break
        }
      }
    }
  }

  const tooltipSeriesHeader = messageUtil.tooltipSeriesHeader(
    widgetSetting.type!,
    widgetSetting.comparisonMode
  )
  return (
    <ErrorBoundary FallbackComponent={({ error }) => <PlaceHolder>{error.message}</PlaceHolder>}>
      <LineChart
        series={formattedSeriesSet}
        timeUnit={widgetSetting.timeUnit!.toLowerCase() as TimeUnit}
        yAxisTitle={yAxisInfoList}
        seriesHeader={tooltipSeriesHeader!}
        colorSettings={SeriesColorSettings}
      />
    </ErrorBoundary>
  )
}

export default Timeline
